// Carer-portal sign-in screen. Mobile-first: full-screen teal background,
// large tap targets, 16px input font (no iOS auto-zoom).

import { useState, type JSX, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { carerApi, setCarerToken, CarerApiError } from "../../lib/carerApi.js";

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function CarerLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await carerApi.login(email.trim(), password);
      if (res.user.role !== "carer") {
        setError("This portal is for care workers only.");
        return;
      }
      setCarerToken(res.access_token, (res as {refresh_token?: string}).refresh_token);
      navigate("/carer", { replace: true });
    } catch (err) {
      if (err instanceof CarerApiError) {
        if (err.status === 401) setError("Invalid email or password.");
        else if (err.status === 429) setError("Too many attempts. Try again in a moment.");
        else setError(err.message);
      } else {
        setError("Network error. Check your connection.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-navy-800 flex flex-col items-center justify-center p-4">
      <div className="mb-8 text-center">
        <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
          <span className="text-4xl" role="img" aria-label="HCM">🏥</span>
        </div>
        <h1 className="text-white text-2xl font-bold">HCM</h1>
        <p className="text-navy-200 text-sm mt-1">Care Worker Portal</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-1">Sign in</h2>
        <p className="text-slate-500 text-sm mb-6">Use your work email and password</p>

        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4" noValidate>
          <div>
            <label htmlFor="carer-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              id="carer-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@agency.com"
              autoComplete="email"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base
                         focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="carer-password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input
              id="carer-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base
                         focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
            />
          </div>

          {error && (
            <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 rounded-xl bg-navy-700 text-white font-semibold
                       text-base hover:bg-navy-800 active:bg-navy-900 disabled:opacity-50
                       transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="text-center mt-4">
          <Link to="/carer/forgot-password" className="text-navy-700 text-sm hover:underline">
            Forgot password?
          </Link>
        </p>
      </div>

      <p className="text-navy-300 text-xs mt-6">HCM — Powered by Nsofar</p>
    </div>
  );
}
